import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ActionIconButton } from '@onesource/components';
import {
  EReviewDueState,
  EReviewMissingProgress,
  EReviewSubmissionStatus,
  type TReviewMissingForm,
  type TReviewMissingItem,
  type TReviewMissingProgress,
} from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { MdNotificationsActive } from 'react-icons/md';

import { REVIEW_STATUS_STYLE } from './review-status-chip';
import { ReviewTeacherCell } from './review-teacher-cell';
import { URGENCY_STYLE, urgencyRowSx, type TUrgency } from './review-urgency';

/** One look per progress, from the status palette: a draft is blue, a rejection red. */
export const MISSING_PROGRESS_STYLE: Record<TReviewMissingProgress, { label: string; bg: string; color: string }> = {
  [EReviewMissingProgress.not_started]: { label: 'Not started', bg: '#f4f4f5', color: '#52525b' },
  [EReviewMissingProgress.started]: {
    label: 'Started',
    bg: REVIEW_STATUS_STYLE[EReviewSubmissionStatus.in_progress].bg,
    color: REVIEW_STATUS_STYLE[EReviewSubmissionStatus.in_progress].color,
  },
  [EReviewMissingProgress.rejected]: {
    label: 'Rejected',
    bg: REVIEW_STATUS_STYLE[EReviewSubmissionStatus.rejected].bg,
    color: REVIEW_STATUS_STYLE[EReviewSubmissionStatus.rejected].color,
  },
};

const day = (value: Date | string) => DateTime.fromJSDate(new Date(value)).toLocaleString(DateTime.DATE_MED);

const shortDay = (value: Date | string) =>
  DateTime.fromJSDate(new Date(value)).toLocaleString({ day: 'numeric', month: 'short' });

/** A form's due state as time pressure; not-due and no-date forms have none. */
const urgencyOf = (form: TReviewMissingForm): TUrgency | null =>
  form.due === EReviewDueState.overdue ? 'overdue' : form.due === EReviewDueState.due_soon ? 'due_soon' : null;

/** The module, the due date, and when the draft or the rejection happened — the detail a chip has no room for. */
const detailOf = (form: TReviewMissingForm): string => {
  const when =
    form.progress === EReviewMissingProgress.rejected && form.rejectedAt
      ? `Rejected ${day(form.rejectedAt)}, not submitted again`
      : form.progress === EReviewMissingProgress.started && form.startedAt
        ? `Draft started ${day(form.startedAt)}, not submitted`
        : 'Not started';
  const due = form.dueAt
    ? `${form.due === EReviewDueState.overdue ? 'Was due' : 'Due'} ${DateTime.fromJSDate(new Date(form.dueAt)).toLocaleString(DateTime.DATETIME_MED)}`
    : null;
  return [form.moduleName, due, when].filter(Boolean).join(' · ');
};

/** A form the student has not submitted. Click it to remind them about this form only. */
const MissingFormChip: FC<{ form: TReviewMissingForm; onRemind: () => void }> = ({ form, onRemind }) => {
  const style = MISSING_PROGRESS_STYLE[form.progress];
  const isNotStarted = form.progress === EReviewMissingProgress.not_started;
  const detail = detailOf(form);
  const urgency = urgencyOf(form);
  const dueText =
    urgency === 'overdue' ? 'Overdue' : urgency === 'due_soon' && form.dueAt ? `Due ${shortDay(form.dueAt)}` : null;
  const reminded = form.lastRemindedAt
    ? `Reminded ${DateTime.fromJSDate(new Date(form.lastRemindedAt)).toRelative()}`
    : null;
  return (
    <Tooltip title={[detail, reminded, 'Click to remind about this form'].filter(Boolean).join(' · ')} arrow>
      <Chip
        size="small"
        clickable
        onClick={onRemind}
        icon={<MdNotificationsActive size={13} />}
        aria-label={`Remind about ${form.formTitle}. ${detail}${reminded ? `. ${reminded}` : ''}`}
        label={
          <>
            {form.formTitle}
            {/* The state is in the words too, not only the colour. */}
            {!isNotStarted && <Box component="span" sx={{ fontWeight: 700 }}>{` · ${style.label}`}</Box>}
            {dueText && (
              <Box component="span" sx={{ fontWeight: 700, color: URGENCY_STYLE[urgency as TUrgency].text }}>
                {` · ${dueText}`}
              </Box>
            )}
          </>
        }
        sx={{
          height: 'auto',
          minHeight: 24,
          maxWidth: '100%',
          // Long form names wrap on a phone instead of being cut off.
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
          fontSize: '0.75rem',
          bgcolor: style.bg,
          color: style.color,
          border: '1px solid',
          borderColor: urgency ? URGENCY_STYLE[urgency].bar : isNotStarted ? 'divider' : 'transparent',
          '& .MuiChip-icon': { color: 'inherit', opacity: 0.7, ml: '6px' },
          '&:hover .MuiChip-icon': { opacity: 1 },
        }}
      />
    </Tooltip>
  );
};

interface ReviewMissingRowProps {
  item: TReviewMissingItem;
  /** Admins see each student's teacher. */
  showTeacher: boolean;
  /** The row's bell: every missing form. A form chip: that form only. */
  onRemind: (item: TReviewMissingItem, form?: TReviewMissingForm) => void;
}

/** A student and the forms they have not submitted, with a reminder button. */
export const ReviewMissingRow: FC<ReviewMissingRowProps> = ({ item, showTeacher, onRemind }) => {
  const rowUrgency: TUrgency | null = item.forms.some(f => f.due === EReviewDueState.overdue)
    ? 'overdue'
    : item.forms.some(f => f.due === EReviewDueState.due_soon)
      ? 'due_soon'
      : null;
  const reminded = item.lastRemindedAt ? DateTime.fromJSDate(new Date(item.lastRemindedAt)).toRelative() : null;
  return (
    <Box
      role="listitem"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        px: { xs: 2, md: 2.5 },
        py: 1.75,
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
        ...urgencyRowSx(rowUrgency),
      }}
    >
      <Avatar src={item.student.avatar ? getMediaUrl(item.student.avatar) : undefined} sx={{ width: 40, height: 40 }}>
        {item.student.name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
          {item.student.name}
        </Typography>
        {item.student.email && (
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
            {item.student.email}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {item.forms.map(form => (
            <MissingFormChip key={form.formId} form={form} onRemind={() => onRemind(item, form)} />
          ))}
        </Box>
      </Box>

      {showTeacher && (
        <Box sx={{ alignSelf: 'center' }}>
          <ReviewTeacherCell teacher={item.teacher} />
        </Box>
      )}

      <Box sx={{ alignSelf: 'center', textAlign: 'right', flexShrink: 0, minWidth: 64 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {item.forms.length} of {item.formsInScope}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          missing
        </Typography>
      </Box>

      <Box sx={{ alignSelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72 }}>
        <ActionIconButton
          tooltip={`Remind ${item.student.name} about every form shown`}
          aria-label={`Remind ${item.student.name} about every form shown`}
          icon={<MdNotificationsActive size={18} />}
          onClick={() => onRemind(item)}
        />
        {reminded && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.68rem', textAlign: 'center', mt: 0.25 }}
          >
            Reminded {reminded}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
